import React from 'react';
import { get, isEmpty, noop } from 'lodash';
import searchQuery from 'query-string';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';

// React view component to render
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { memberDetailsHeaderFamilyUnit as MemberDetailsHeaderFamilyUnitView } from 'ui/views/widgets/memberDetailsHeaderFamilyUnit';
import { MemberDetailsHeaderFamilyUnitWidgetService } from 'services/memberDetailsHeaderFamilyUnitWidget';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import { REDUCER } from 'constants/entity-names';
import { updateCallState, initCallState } from 'store/actions/callState';
import { clearRxCheckoutState } from 'resolvers/checkoutState';
import { GlobalService } from 'services/global';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberDetailsHeaderFamilyUnitWidget } from 'ui/containers/MemberDetailsHeaderFamilyUnitWidget';
 *
 * // render
 * <MemberDetailsHeaderFamilyUnitWidget propName='propValue'/>
 */
class _MemberDetailsHeaderFamilyUnitWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind events and methods
        this.handleFamilyMemberClick = this.handleFamilyMemberClick.bind( this );
        this.handleSwitchMemberAction = this.handleSwitchMemberAction.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
        };

        // create service instance
        this.memberDetailsHeaderFamilyUnitWidgetService = new MemberDetailsHeaderFamilyUnitWidgetService();
        this.globalService = new GlobalService();

        // start component data fetch
        this.fetchComponentData();
    }

    /******************************************************/

    /**
     * @desc Fetch member detail information.
     */
    async fetchComponentData() {
        log.debug( 'MemberDetailsHeaderFamilyUnitWidget.memberDetailsHeaderFamilyUnitWidgetService()' );

        // cancel ongoing request
        this.memberDetailsHeaderFamilyUnitWidgetService.cancel();

        try {
            const { status, data }  = await this.memberDetailsHeaderFamilyUnitWidgetService.fetchData( {
                url: this.props.widget.url,
                data: this.props.requestPayload,
            } );

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
            } else {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    componentData: data,
                } );
            }

        } catch( e ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
            } );
        }
    }

    /******************************************************/

    /**
     * @desc Handle Switch Member Action.
     */
    handleSwitchMemberAction() {
        log.debug( 'MemberDetailsHeaderFamilyUnitWidget.handleSwitchMemberAction()' );

        // switch to authorization - member verification step
        this.props.history.push( {
            pathname: DASHBOARD_SUB_ROUTES.CALL_AUTHORIZATION_FLOW,
            search: searchQuery.stringify( {
                verifyOther: true,
                step: 2,
            } ),
        } );
    }

    /**
     * @desc when a family member is clicked.
     */
    handleFamilyMemberClick( familyMember ) {
        log.debug( 'MemberDetailsHeaderFamilyUnitWidget.handleFamilyMemberClick()' );

        // ignore if selected member is clicked
        if( this.props.memberId === familyMember.id ) {
            return;
        }

        // clear checkout state if any
        clearRxCheckoutState();

        const selection = {
            memberId: familyMember.id,
            patientId: null !== familyMember.patientId && undefined !== familyMember.patientId ? familyMember.patientId : '',
            liveFallback: true === get( this.props, 'requestPayload.context.liveFallback', false )
        };

        /*---------*/

        if( isEmpty( this.props.callState ) ) {

            // reload route with new member id
            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
                state: selection
            } );

            // execute member switch callback
            this.props.onMemberSwitch( selection );

            // generate off call action log
            setTimeout( () => {
                this.props.onGenerateActionListLog();
            } );

        } else if( familyMember.verified ) {

            // start calculating alerts
            this.globalService.startAlertsCalculation( { memberId: familyMember.id } );

            // reload route with new member id
            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
                state: selection
            } );

            // execute member switch callback
            this.props.onMemberSwitch( selection );
            
        } else {

            // switch to authorization - member verification step
            this.props.history.push( {
                pathname: DASHBOARD_SUB_ROUTES.CALL_AUTHORIZATION_FLOW,
                search: searchQuery.stringify( {
                    step: 2,
                } ),
                state: selection
            } );
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.memberDetailsHeaderFamilyUnitWidgetService.cancel(); // cancel ongoing requests
        this.globalService.cancel();
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberDetailsHeaderFamilyUnitView
                memberId={ this.props.memberId }
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                callState={ this.props.callState }
                className={ this.props.className }
                { ...get( this.state, 'componentData', {} ) }
                onFamilyMemberClick={ this.handleFamilyMemberClick }
                onSwitchMemberClick={ this.handleSwitchMemberAction }/>
        );
    }
}

// set display name
_MemberDetailsHeaderFamilyUnitWidget.displayName = '_MemberDetailsHeaderFamilyUnitWidget';

// set default props
_MemberDetailsHeaderFamilyUnitWidget.defaultProps = {
    memberId: null,
    requestPayload: {},
    onMemberSwitch: noop,
};

// prop types
_MemberDetailsHeaderFamilyUnitWidget.propTypes = {};

/*********************************************/

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
        appContext: state[ REDUCER.APP_CONTEXT ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        initCallState: bindActionCreators( initCallState, dispatch ),
        updateCallState: bindActionCreators( updateCallState, dispatch ),
    };
};

// return component with acccess to Redux store
export const MemberDetailsHeaderFamilyUnitWidget = withRouter( connect( mapStateToProps, mapDispatchToProps )( _MemberDetailsHeaderFamilyUnitWidget ) );
